import { type ComponentProps, memo, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';
import { formatVideoTimecodeValue, getVideoTimecode } from '@air/utils-video';

import { usePlayerStore } from './PlayerContextProvider';
import { PlayerTimeFormatTimecodeFrames } from './PlayerTimeFormatTimecodeFrames';

export type PlayerTimeFormatTimecodeProps = ComponentProps<'div'> & {
  frameCount?: number;
};

export const PlayerTimeFormatTimecode = memo(
  ({ className, frameCount, ...restOfProps }: PlayerTimeFormatTimecodeProps) => {
    const currentTime = usePlayerStore((state) => state.currentTime);
    const duration = usePlayerStore((state) => state.duration);
    const { hours, minutes, seconds, frames } = useMemo(() => {
      return getVideoTimecode({
        currentTime,
        frameCount,
        totalTime: duration,
      });
    }, [currentTime, duration, frameCount]);

    return (
      <div
        className={cn('flex items-center gap-1 tabular-nums', className)}
        data-testid="PLAYER_TIME_FORMAT_TIMECODE"
        {...restOfProps}
      >
        <span data-testid="PLAYER_TIME_FORMAT_TIMECODE_HOURS">{formatVideoTimecodeValue({ value: hours })}</span>
        <span>:</span>
        <span data-testid="PLAYER_TIME_FORMAT_TIMECODE_MINUTES">{formatVideoTimecodeValue({ value: minutes })}</span>
        <span>:</span>
        <span data-testid="PLAYER_TIME_FORMAT_TIMECODE_SECONDS">{formatVideoTimecodeValue({ value: seconds })}</span>
        {frames !== undefined && (
          <>
            <span>:</span>
            <PlayerTimeFormatTimecodeFrames frames={frames} />
          </>
        )}
      </div>
    );
  },
);

PlayerTimeFormatTimecode.displayName = 'PlayerTimeFormatTimecode';
